import EmptyStateNotice from '@/components/EmptyStateNotice';
import EnvironmentTag from '@/components/EnvironmentTag';
import Input from '@/components/Input';
import ActivityFilter from '@/components/modals/workflowLogsModal/ActivityFilter';
import AdditionalFiltersButton from '@/components/modals/workflowLogsModal/AdditionalFiltersButton';
import DateRangeFilter from '@/components/modals/workflowLogsModal/DateRangeFilter';
import EnvironmentFilter from '@/components/modals/workflowLogsModal/EnvironmentFilter';
import SnapshotFilter from '@/components/modals/workflowLogsModal/SnapshotFilter';
import StatusFilter from '@/components/modals/workflowLogsModal/StatusFilter';
import StatusMarker from '@/components/modals/workflowLogsModal/StatusMarker';
import { BsFlag, FiPlay, FiSearch, LuAlignLeft } from '@/components/react-icons/main';
import Select from '@/components/Select';
import Tag from '@/components/Tag';
import TextWithEllipsis from '@/components/TextWithEllipsis';
import DividerV2 from '@/components/v2/DividerV2';
import IconV2 from '@/components/v2/IconV2';
import RowV2 from '@/components/v2/RowV2';
import ScrollContainerV2 from '@/components/v2/ScrollContainerV2';
import useResponsive from '@/hooks/useResponsive';
import debounce from '@/lib/debounce';
import setQuery from '@/lib/setQuery';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import selectors from '@/redux/selectors/selectors';
import runWorkflow from '@/screens/workflows/redux/actions/runWorkflow';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import utilityAppIcons from '@/screens/workflows/schema/utilityAppIcons';
import getLogTriggerMessage from '@/screens/workflows/workflowLogs/lib/getLogTriggerMessage';
import LogEntriesPlaceholder from '@/screens/workflows/workflowLogs/LogEntriesPlaceholder';
import LogEntry from '@/screens/workflows/workflowLogs/LogEntry';
import { workflowStatusMap } from '@shared/schema/workflowStatusMap.js';
import { formatDuration } from '@shared/utils/formatDuration.js';
import { formatShortTimestamp } from '@shared/utils/formatShortTimestamp.js';
import { differenceInSeconds } from 'date-fns';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const statusDisplayMap = {
  ...workflowStatusMap,
  6: 'Limit Exceeded'
};

const WorkflowLogsModalListing = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const querySearchValue = useArgSelector(selectors.query, 'search');
  const workflowId = useSelector(selectors.workflowId);
  const logId = useArgSelector(selectors.query, 'logId');
  const loadingLogs = useArgSelector(selectors.isLoading, 'getWorkflowLogs');
  const loadingSnapshots = useArgSelector(selectors.isLoading, 'getWorkflowSnapshots');
  const logs = useSelector(workflowScreenSelectors.activeWorkflowLogs);
  const canRunWorkflow = useArgSelector(selectors.permission, 'workflow:run');
  const workflowName = useSelector(workflowScreenSelectors.activeWorkflow)?.name;
  const { tablet } = useResponsive();
  const environments = useSelector(selectors.environments) || [];
  const workflowSnapshots = useSelector((state) => workflowScreenSelectors.activeWorkflowSnapshots(state)) || [];

  const loading = loadingLogs || loadingSnapshots;
  const searchValue = querySearchValue ? decodeURIComponent(querySearchValue.replace(/\+/g, ' ')) : '';

  useEffect(() => {
    setSearchInput(searchValue);
  }, [searchValue]);

  const debouncedSearch = useCallback(
    debounce((value) => setQuery({ search: value || null }), 500),
    []
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleRunClick = async () => {
    setIsRunning(true);
    await waitForSyncReady();
    runWorkflow({ workflowId }, () => setIsRunning(false));
  };

  const handleSummaryClick = () => {
    setQuery({ logId: 'summary', time: 0, logStepId: null, logTab: null });
  };

  const getStartedActions = canRunWorkflow ? [{ label: 'Run', icon: FiPlay, onClick: handleRunClick, className: 'primary', loading: isRunning }] : [];

  useEffect(() => {
    if (!logId && workflowId) {
      setQuery({ logId: 'summary' });
    }
  }, [workflowId, logId]);

  const dateRangeFilter = useArgSelector(selectors.pref, `workflowLogsDateRangeFilter-${workflowId}`);
  const snapshotFilter = useArgSelector(selectors.pref, `workflowLogsSnapshotFilter-${workflowId}`);
  const environmentFilter = useArgSelector(selectors.pref, `workflowLogsEnvironmentFilter-${workflowId}`);
  const statusFilter = useArgSelector(selectors.pref, `workflowLogsStatusFilter-${workflowId}`);
  const activityFilter = useArgSelector(selectors.pref, `workflowLogsActivityFilter-${workflowId}`);
  const endUserFilter = useArgSelector(selectors.pref, `workflowLogsEndUserFilter-${workflowId}`);
  const installationFilter = useArgSelector(selectors.pref, `workflowLogsInstallationFilter-${workflowId}`);

  const isAllTimeWithNoFilters = () => {
    const isAllTime = !dateRangeFilter || (dateRangeFilter.from === null && dateRangeFilter.to === null);
    const hasNoActivityFilter = Array.isArray(activityFilter) ? !activityFilter?.length : !activityFilter;
    const hasNoOtherFilters = !snapshotFilter?.length && !environmentFilter?.length && !statusFilter?.length && !endUserFilter?.length && !installationFilter?.length && hasNoActivityFilter && !searchValue;
    return isAllTime && hasNoOtherFilters;
  };

  const buildLogEntryRightContent = (log) => {
    const { startedOn, finishedOn, updatedOn } = log;
    const isRunning = log?.startedOn && !log?.finishedOn && log?.statusId === 1;
    const duration = differenceInSeconds(new Date(finishedOn || updatedOn), new Date(startedOn));
    const timeAgo = formatShortTimestamp(startedOn);
    const environment = environments.find((e) => e.id === log?.environmentId);
    const workflowSnapshotVersion = workflowSnapshots.find((snapshot) => snapshot.id === log?.snapshotId)?.version || 'Draft';
    const environmentLabel = environment?.name || '';
    const environmentName = `${environmentLabel || 'Production'} - ${workflowSnapshotVersion}`;
    const environmentDataTitle = `${environmentLabel ? `${environmentLabel} Environment - ` : ''}Workflow Version ${workflowSnapshotVersion}`;
    const triggerStep = log?.meta?.triggerStep;
    const triggerStepMessage = triggerStep ? getLogTriggerMessage(triggerStep) : null;

    return (
      <Fragment>
        {timeAgo && (
          <TextWithEllipsis className={'time-ago'} title={new Date(startedOn).toLocaleDateString('en-US', { hour: 'numeric', minute: 'numeric' })}>
            {timeAgo}
          </TextWithEllipsis>
        )}
        <Tag>{`${isRunning ? 'Running for ' : ''}${formatDuration(duration)}`}</Tag>
        {triggerStep?.appSlug && <IconV2 icon={utilityAppIcons[triggerStep?.appSlug]} className="light-medium" size={12} title={triggerStepMessage} />}
        <EnvironmentTag environmentId={log?.environmentId} overrideName={environmentName} data-title={environmentDataTitle} />
      </Fragment>
    );
  };

  const buildLogEntryLabel = (log) => {
    const { statusId } = log;
    return statusDisplayMap[statusId] || 'Unknown Status';
  };

  const getLogEntryDisplayLabel = (log) => {
    const { statusId } = log;
    return statusDisplayMap[statusId] || 'Unknown Status';
  };

  const selectOptions = useMemo(() => {
    const options = [];

    if (!loading) {
      options.push({
        value: 'summary',
        label: 'Logs Summary',
        displayLabel: 'Logs Summary',
        icon: LuAlignLeft,
        right: null
      });
    }

    if (logs?.length) {
      logs.forEach((log) => {
        options.push({
          value: log.id,
          label: buildLogEntryLabel(log),
          displayLabel: getLogEntryDisplayLabel(log),
          icon: <StatusMarker log={log} />,
          right: buildLogEntryRightContent(log),
          timeAgo: formatShortTimestamp(log.startedOn)
        });
      });
    }

    return options;
  }, [logs, loading, environments, workflowSnapshots]);

  const handleSelectChange = (option) => {
    if (!option) {
      return;
    }

    if (option.value === 'summary') {
      handleSummaryClick();
    } else {
      setQuery({ logId: option.value, time: 0, logStepId: null, logTab: null });
    }
  };

  const selectedOption = selectOptions.find((opt) => opt.value === logId || (logId === 'summary' && opt.value === 'summary'));
  const customDisplayValue = selectedOption?.value === 'summary' ? 'Logs Summary' : `${selectedOption?.displayLabel} (${selectedOption?.timeAgo})`;

  return (
    <div className="workflow-logs-modal-listing">
      <h2>{workflowName}</h2>
      <Input className="search-input" size="md" icon={FiSearch} value={searchInput} onChange={handleSearch} placeholder="Search logs steps" autoFocus />
      <div className="filters">
        <DateRangeFilter />
        <SnapshotFilter />
        <EnvironmentFilter />
        <StatusFilter />
        <ActivityFilter />
        <AdditionalFiltersButton />
      </div>
      {!tablet && <DividerV2 />}
      {tablet ? (
        <>
          {loading && <LogEntriesPlaceholder />}
          {!loading && (
            <>
              {selectOptions.length > 0 ? (
                <Select //
                  value={selectedOption}
                  options={selectOptions}
                  onChange={handleSelectChange}
                  placeholder="Select a log entry..."
                  showOptionIcon
                  customValue={customDisplayValue}
                  floatingSelectV2Props={{ className: 'log-entry-select' }}
                />
              ) : isAllTimeWithNoFilters() ? (
                <EmptyStateNotice icon={BsFlag} title="Logs" description="Execute the workflow once to start generating log entries." actions={getStartedActions} />
              ) : (
                <EmptyStateNotice icon={BsFlag} title="No logs found" description="No logs were found for this time range." />
              )}
            </>
          )}
        </>
      ) : (
        <ScrollContainerV2 className={'log-entries'}>
          {!loading && (
            <RowV2 className={`log-entry`} onClick={handleSummaryClick} active={logId === 'summary'}>
              <RowV2.Left>
                <div className="status-container">
                  <IconV2 icon={LuAlignLeft} size={8} className="light-medium" />
                  <TextWithEllipsis className="entry-message">
                    <span>Logs Summary</span>
                  </TextWithEllipsis>
                </div>
              </RowV2.Left>
            </RowV2>
          )}

          {loading && <LogEntriesPlaceholder />}
          {!!logs?.length && !loading && logs?.map((n, idx) => <LogEntry key={idx} log={n} />)}
          {!loading && !logs?.length && isAllTimeWithNoFilters() && <EmptyStateNotice icon={BsFlag} title="Logs" description="Execute the workflow once to start generating log entries." actions={getStartedActions} />}
          {!loading && !logs?.length && !isAllTimeWithNoFilters() && <EmptyStateNotice icon={BsFlag} title="No logs found" description="No logs were found for this time range." />}
        </ScrollContainerV2>
      )}
    </div>
  );
};

export default WorkflowLogsModalListing;
